import { EVENTS_CHANNEL, queryKeys, useLiveServers, useSeasons } from '@ottd/api';
import type { ServerSummary } from '@ottd/api';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { FilterTabs } from '@/components/prototype/filter-tabs';
import type { TypeFilter } from '@/components/prototype/filter-tabs';
import { useEchoChannel } from '@/hooks/use-echo-channel';
import { PanelErrorBoundary } from '@/components/prototype/panel-error-boundary';
import { DiscordChat } from '@/pages/home/discord-chat';
import { Hero } from '@/pages/home/hero';
import { ServerLiveCard } from '@/pages/home/server-live-card';
import { ServerLog } from '@/pages/home/server-log';

/*
 * The bottom row is bounded and both panels fill it, so they end level and
 * neither is padded out to match the other. Written as a literal class so
 * Tailwind sees it.
 */
const PANEL_HEIGHT = 'h-[520px]';

/** Live-grid poll while the realtime socket is down. */
const OFFLINE_POLL_MS = 15_000;

/*
 * Slow refresh even while the socket is up: game events are what push the
 * grid, but the goal bars and in-game clocks move on every snapshot, and a
 * quiet game can go minutes without an event. The endpoint is cached 5s.
 */
const CONNECTED_POLL_MS = 30_000;

function matchesFilter(server: ServerSummary, filter: TypeFilter): boolean {
  return filter === 'all' || server.type === filter;
}

export default function HomePage() {
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');
  const [filter, setFilter] = useState<TypeFilter>('all');
  const queryClient = useQueryClient();

  // Realtime: refetch the live seed when the global feed announces an event, so
  // the feed/grid stay push-driven with the server's canonical formatting.
  const connected = useEchoChannel(EVENTS_CHANNEL, {
    'game.event': () => void queryClient.invalidateQueries({ queryKey: queryKeys.live.servers() }),
  });

  const { data: live } = useLiveServers(connected ? CONNECTED_POLL_MS : OFFLINE_POLL_MS);
  const { data: seasonsPage } = useSeasons(1);

  const currentSeason = seasonsPage?.data.find((s) => s.isCurrent) ?? seasonsPage?.data[0] ?? null;

  const liveServers = useMemo(() => (live?.servers ?? []).filter((s) => s.isLive), [live?.servers]);
  const filtered = liveServers.filter((s) => matchesFilter(s, filter));

  return (
    <div>
      <Hero
        seasonNum={currentSeason?.num ?? null}
        gamesCount={currentSeason?.gamesCount ?? null}
        endsInDays={currentSeason?.endsInDays ?? null}
        seasonsTotal={seasonsPage?.meta.total ?? null}
        serversLive={liveServers.length}
        podium={currentSeason?.podium ?? []}
      />

      <section className="mx-auto max-w-[1280px] px-4 pt-8 sm:px-5">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-strong text-lg">{t('liveNow.title')}</h2>
          <span className="text-green font-mono text-xs">
            {t('liveNow.running', { count: liveServers.length })}
          </span>
          <FilterTabs value={filter} onChange={setFilter} className="ml-1" />
          <Link to="/server" className="text-orange ml-auto inline-flex items-center gap-1 text-sm">
            {tc('allServers')}
            <ArrowRight className="size-3.5" aria-hidden />
          </Link>
        </div>

        {/* Equal rows: a card with no goal bar yet stays as tall as one with. */}
        <div className="mt-4 grid auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.length === 0 ? (
            <p className="text-dim col-span-full font-mono text-sm">{t('liveNow.empty')}</p>
          ) : (
            filtered.map((server) => <ServerLiveCard key={server.id} server={server} />)
          )}
        </div>
      </section>

      <section className="mx-auto grid max-w-[1280px] gap-4 px-4 py-8 sm:px-5 lg:grid-cols-2">
        <BevelWindow
          title={t('activity.title')}
          chipColor="var(--panel)"
          bodyClassName="flex min-h-0 flex-1 flex-col p-0"
          shadow="sm"
          className={`flex flex-col ${PANEL_HEIGHT}`}
        >
          <ServerLog entries={live?.feed ?? []} />
        </BevelWindow>

        <PanelErrorBoundary title={t('discord.title')}>
          <DiscordChat className={PANEL_HEIGHT} />
        </PanelErrorBoundary>
      </section>
    </div>
  );
}
